import {
  ABILITIES,
  EMOTES,
  QUICK_MESSAGES,
  ROOM_LIMITS,
  SPECIAL_ABILITIES,
  VALVE_COLOR_NAMES,
  type ReportReason,
  type RoomPlayerInfo,
  type SpecialAbilityId,
} from '@kokun/shared';
import { live, ui, type GameUI } from '../../app/game-store';
import { keyLabel, settings } from '../../app/settings';
import { connection } from '../../multiplayer/connection';
import { audio } from '../../game/audio/audio';
import type { Input } from '../../game/core/input';
import { GameSession } from '../../game/world/game-session';
import { append, button, clear, formatTime, h, modal, toast } from '../dom';
import { openSettings } from './settings-panel';
import { openHowToPlay } from './how-to-play';

export interface HudActions {
  leave(): void;
  openCreator(): void;
  session(): GameSession | null;
  input: Input;
  engineInfo(): { renderer: string; maxTexture: number };
}

const REPORT_REASONS: Record<ReportReason, string> = {
  harassment: 'Acoso o bromas abusivas',
  cheating: 'Trampas',
  'offensive-name': 'Nombre ofensivo',
  'offensive-avatar': 'Avatar o foto inapropiada',
  spam: 'Spam en el chat',
  other: 'Otro motivo',
};

export function mountHud(a: HudActions): () => void {
  const root = h('div', { class: 'hud', 'data-testid': 'hud' });
  const top = h('div', { class: 'hud-top' });
  const missionBox = h('div', { class: 'mission panel glass', 'data-testid': 'mission' });
  const statusBox = h('div', { class: 'status-box' });
  const banner = h('div', { class: 'banner' });
  const center = h('div', { class: 'center-msg' });
  const prompt = h('div', { class: 'prompt' });
  const abilities = h('div', { class: 'abilities' });
  const emoteBar = h('div', { class: 'emote-bar' });
  const lobby = h('div', { class: 'lobby panel glass', 'data-testid': 'lobby' });
  const chatLog = h('div', { class: 'chat-log', 'aria-live': 'polite' });
  const chatInput = h('input', { class: 'chat-input', maxLength: 160, placeholder: 'Escribe y pulsa Intro…  (Esc para cerrar)' }) as HTMLInputElement;
  const quick = h('div', { class: 'quick-msgs' });
  const chat = h('div', { class: 'chat' }, chatLog, quick, chatInput);
  const waypointLayer = h('div', { class: 'waypoints' });
  const floaterLayer = h('div', { class: 'floaters' });
  const scoreboard = h('div', { class: 'scoreboard panel glass hidden' });
  const results = h('div', { class: 'results-wrap' });
  const pause = h('div', { class: 'pause-wrap' });
  const paint = h('div', { class: 'paint-overlay' });
  const fps = h('div', { class: 'fps' });
  const touch = h('div', { class: 'touch' });
  top.append(missionBox, banner, statusBox);
  root.append(paint, waypointLayer, floaterLayer, top, center, prompt, lobby, chat, abilities, emoteBar, scoreboard, results, pause, fps, touch);
  document.body.append(root);

  // ------------------------------------------------------------ chat
  let chatOpen = false;
  const openChat = (open: boolean) => {
    chatOpen = open;
    chat.classList.toggle('open', open);
    a.input.enabled = !open && !ui.get().paused;
    if (open) {
      a.input.unlock();
      setTimeout(() => chatInput.focus(), 0);
    } else chatInput.blur();
  };
  chatInput.addEventListener('keydown', (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      const text = chatInput.value.trim();
      if (text) connection.emit('chat:send', { text });
      chatInput.value = '';
      openChat(false);
    } else if (e.key === 'Escape') openChat(false);
  });
  QUICK_MESSAGES.forEach((q, i) => quick.append(button(q, () => (connection.emit('chat:send', { quick: i }), openChat(false)), 'chip')));

  const renderChat = (s: GameUI) => {
    clear(chatLog);
    for (const m of s.chat.slice(-12))
      chatLog.append(
        h('div', { class: `msg${m.system ? ' sys' : ''}${m.quick ? ' quick' : ''}` }, m.system ? null : h('b', null, `${m.name}: `), m.text),
      );
    chatLog.scrollTop = chatLog.scrollHeight;
  };

  // ------------------------------------------------------------ menú de jugador (bloquear / reportar / expulsar)
  const playerMenu = (p: RoomPlayerInfo) => {
    const s = ui.get();
    const isHost = s.room?.hostId === s.you;
    const blocked = s.profile?.blocked.includes(p.id) ?? false;
    const reason = h('select', null, ...Object.entries(REPORT_REASONS).map(([k, v]) => h('option', { value: k }, v))) as HTMLSelectElement;
    const details = h('input', { placeholder: 'Detalles (opcional)', maxLength: 300 }) as HTMLInputElement;
    modal(`${p.name} #${p.tag}`, [h('p', null, `Nivel ${p.level}`), h('h3', null, 'Reportar'), reason, details], [
      {
        label: blocked ? 'Desbloquear' : 'Bloquear',
        cls: 'btn ghost',
        run: async () => {
          await connection.request('player:block', { playerId: p.id, blocked: !blocked });
          const prof = ui.get().profile!;
          ui.set({ profile: { ...prof, blocked: blocked ? prof.blocked.filter((x) => x !== p.id) : [...prof.blocked, p.id] } });
          toast(blocked ? 'Jugador desbloqueado.' : 'Jugador bloqueado: no verás su chat ni su foto.', 'success');
        },
      },
      ...(isHost ? [{ label: 'Expulsar', cls: 'btn danger', run: async () => void (await connection.request('room:kick', { playerId: p.id }).catch((e) => toast(e.message, 'error'))) }] : []),
      {
        label: 'Enviar reporte',
        cls: 'btn primary',
        run: async () => {
          try {
            await connection.request('player:report', { playerId: p.id, reason: reason.value, details: details.value || undefined });
            toast('Reporte enviado. ¡Gracias por ayudar!', 'success');
          } catch (e) {
            toast((e as Error).message, 'error');
          }
        },
      },
    ]);
  };

  // ------------------------------------------------------------ lobby
  let lobbyCollapsed = false;
  const renderLobby = (s: GameUI) => {
    const r = s.room;
    lobby.classList.toggle('hidden', !r || r.phase !== 'lobby');
    if (!r || r.phase !== 'lobby') return;
    clear(lobby);
    const isHost = r.hostId === s.you;
    const me = r.players.find((p) => p.id === s.you);
    const invite = `${location.origin}/?sala=${r.code}`;
    lobby.classList.toggle('collapsed', lobbyCollapsed);
    lobby.append(
      h('div', { class: 'lobby-head' }, h('h2', null, r.config.name), button(lobbyCollapsed ? '▸' : '▾', () => ((lobbyCollapsed = !lobbyCollapsed), renderLobby(ui.get())), 'btn ghost small icon', { 'aria-label': 'Plegar panel' })),
    );
    if (lobbyCollapsed) return;
    append(lobby, [
      h(
        'div',
        { class: 'code-box' },
        h('span', { class: 'small muted' }, 'Código de sala'),
        h('span', { class: 'code', 'data-testid': 'room-code' }, r.code),
        button('📋 Copiar invitación', () => {
          void navigator.clipboard?.writeText(invite).then(() => toast('Enlace copiado: ¡pásaselo a tus amigos!', 'success'), () => toast(invite));
        }, 'btn small'),
      ),
      h('p', { class: 'small muted' }, `${r.players.length}/${r.config.maxPlayers} jugadores · mínimo ${r.minPlayers} · ${r.config.visibility === 'public' ? 'Pública' : 'Privada'}`),
      h(
        'ul',
        { class: 'players' },
        ...r.players.map((p) =>
          h(
            'li',
            { class: `${p.connected ? '' : 'off'}${p.id === s.you ? ' me' : ''}` },
            h('span', null, `${p.isHost ? '👑 ' : ''}${p.name}`, h('small', null, ` #${p.tag} · Nv ${p.level}`)),
            h('span', { class: 'small' }, p.connected ? `${ABILITIES[p.special].icon}` : '📡 reconectando'),
            p.id !== s.you ? button('⋯', () => playerMenu(p), 'btn ghost small icon', { 'aria-label': `Opciones de ${p.name}` }) : null,
          ),
        ),
      ),
      h('h3', null, 'Tu habilidad especial (Q)'),
      h(
        'div',
        { class: 'special-pick' },
        ...SPECIAL_ABILITIES.map((id: SpecialAbilityId) =>
          button(`${ABILITIES[id].icon} ${ABILITIES[id].name}`, () => void connection.request('loadout:set', { special: id }).catch((e) => toast(e.message, 'error')), `chip${me?.special === id ? ' on' : ''}`, { title: ABILITIES[id].description }),
        ),
      ),
      isHost
        ? h(
            'div',
            { class: 'host-cfg' },
            h('h3', null, 'Configuración (anfitrión)'),
            h(
              'label',
              { class: 'check' },
              h('input', { type: 'checkbox', checked: r.config.pranks, onChange: (e: Event) => void connection.request('room:config', { pranks: (e.target as HTMLInputElement).checked }).catch((er) => toast(er.message, 'error')) }),
              ' Bromas activadas',
            ),
            h(
              'label',
              { class: 'check' },
              h('input', { type: 'checkbox', checked: r.config.minigame, onChange: (e: Event) => void connection.request('room:config', { minigame: (e.target as HTMLInputElement).checked }).catch((er) => toast(er.message, 'error')) }),
              ' Minijuego final',
            ),
            h(
              'label',
              { class: 'check' },
              h('input', { type: 'checkbox', checked: r.config.visibility === 'public', onChange: (e: Event) => void connection.request('room:config', { visibility: (e.target as HTMLInputElement).checked ? 'public' : 'private' }).catch((er) => toast(er.message, 'error')) }),
              ' Sala pública',
            ),
            h(
              'label',
              { class: 'set-row' },
              'Máx. jugadores',
              h(
                'select',
                { onChange: (e: Event) => void connection.request('room:config', { maxPlayers: Number((e.target as HTMLSelectElement).value) }).catch((er) => toast(er.message, 'error')) },
                ...Array.from({ length: ROOM_LIMITS.ABSOLUTE_MAX_PLAYERS - 1 }, (_, i) => i + 2).map((n) => h('option', { value: n, selected: n === r.config.maxPlayers }, String(n))),
              ),
            ),
          )
        : null,
      h(
        'div',
        { class: 'lobby-actions' },
        button('🎨 Personaje', a.openCreator, 'btn small'),
        button('🚪 Salir', a.leave, 'btn ghost small'),
        isHost
          ? button('🚀 ¡Empezar misión!', () => void connection.request('room:start').catch((e) => toast(e.message, 'error')), 'btn primary', { 'data-testid': 'start' })
          : h('span', { class: 'small muted' }, 'Esperando a que el anfitrión empiece…'),
      ),
      h('p', { class: 'small muted' }, 'Mientras esperas, explora Kokun City. ¡Prueba el botón rojo de la plaza!'),
      ]);
  };

  // ------------------------------------------------------------ misión
  const renderMission = (s: GameUI) => {
    clear(missionBox);
    const m = s.mission;
    const mg = s.minigame;
    if (mg) {
      const scores = Object.entries(mg.scores).sort((x, y) => y[1] - x[1]);
      missionBox.append(
        h('h2', null, `🪙 ${mg.name}`),
        h('p', { class: 'small' }, '¡Recoge monedas en la plaza! Los empujones valen.'),
        h('ol', { class: 'mini-scores' }, ...scores.slice(0, 6).map(([id, n]) => h('li', { class: id === s.you ? 'me' : '' }, `${s.room?.players.find((p) => p.id === id)?.name ?? '?'} — ${n}`))),
      );
      missionBox.classList.remove('hidden');
      return;
    }
    if (!m || s.mode !== 'online') {
      missionBox.classList.toggle('hidden', s.mode !== 'offline');
      if (s.mode === 'offline') missionBox.append(h('h2', null, '🏃 Modo práctica'), h('p', { class: 'small' }, 'Explora Kokun City sin conexión. Las misiones y bromas necesitan el servidor.'));
      return;
    }
    missionBox.classList.remove('hidden');
    missionBox.append(
      h('div', { class: 'mission-head' }, h('h2', null, m.name), h('span', { class: 'timer', 'data-timer': m.endsAt }, formatTime(m.endsAt - connection.serverNow()))),
      h(
        'ul',
        { class: 'objectives' },
        ...m.objectives.map((o, i) =>
          h(
            'li',
            { class: o.status },
            h('span', { class: 'obj-title' }, o.status === 'done' ? '✅ ' : o.status === 'active' ? '▶ ' : '○ ', o.title),
            o.status === 'active'
              ? h(
                  'div',
                  null,
                  h('div', { class: 'bar' }, h('i', { style: { width: `${(o.progress / o.total) * 100}%` } })),
                  h('p', { class: 'hint' }, o.hint),
                  o.id === 'calibrate' && i === m.stageIndex ? h('p', { class: 'hint small' }, `Colores posibles: ${VALVE_COLOR_NAMES.join(', ')}`) : null,
                )
              : null,
          ),
        ),
      ),
    );
  };

  // ------------------------------------------------------------ habilidades
  const renderAbilities = (s: GameUI) => {
    clear(abilities);
    const me = s.room?.players.find((p) => p.id === s.you);
    if (s.mode !== 'online' || !me) return;
    const b = settings.get().bindings;
    const slot = (id: keyof typeof ABILITIES, key: string) =>
      h(
        'button',
        { class: 'ability', type: 'button', 'data-ability': id, title: ABILITIES[id].description, onClick: () => a.session()?.useAbility(id) },
        h('span', { class: 'ico' }, ABILITIES[id].icon),
        h('span', { class: 'key' }, keyLabel(key)),
        h('span', { class: 'cd' }),
      );
    abilities.append(slot('push', b.prank), slot(me.special, b.special));
  };

  const renderEmotes = () => {
    clear(emoteBar);
    const level = ui.get().profile?.player.level ?? 1;
    EMOTES.forEach((e, i) =>
      emoteBar.append(button([e.icon, h('small', null, String(i + 1))], () => a.session()?.emote(i + 1), `emote${e.unlockLevel > level ? ' locked' : ''}`, { title: `${e.name}${e.unlockLevel > level ? ` (nivel ${e.unlockLevel})` : ''}` })),
    );
  };

  // ------------------------------------------------------------ marcador (Tab)
  const renderScoreboard = (s: GameUI) => {
    scoreboard.classList.toggle('hidden', !s.scoreboard || !s.room);
    if (!s.scoreboard || !s.room) return;
    clear(scoreboard);
    const r = s.room;
    scoreboard.append(
      h('h2', null, `${r.config.name} · ${r.code}`),
      h('p', { class: 'small muted' }, `Fase: ${{ lobby: 'Lobby', countdown: 'Cuenta atrás', mission: 'Misión', minigame: 'Minijuego', results: 'Resultados' }[r.phase]} · ping ${connection.state.get().rtt} ms`),
      h(
        'table',
        null,
        h('tr', null, h('th', null, 'Jugador'), h('th', null, 'Nivel'), h('th', null, 'Puntos'), h('th', null, 'Estado')),
        ...[...r.players].sort((x, y) => y.score - x.score).map((p) => h('tr', { class: p.id === s.you ? 'me' : '' }, h('td', null, `${p.isHost ? '👑 ' : ''}${p.name} #${p.tag}`), h('td', null, String(p.level)), h('td', null, String(p.score)), h('td', null, p.connected ? '🟢' : '🟠'))),
      ),
    );
  };

  // ------------------------------------------------------------ resultados
  const renderResults = (s: GameUI) => {
    clear(results);
    const r = s.results;
    if (!r) return;
    a.input.unlock();
    const me = r.players.find((p) => p.id === s.you);
    results.append(
      h(
        'div',
        { class: 'results panel', 'data-testid': 'results' },
        h('h1', { class: r.success ? 'win' : 'lose' }, r.success ? '¡KOKUN CITY TIENE LUZ! 💡' : 'Kokun City sigue a oscuras… 🌑'),
        h('p', { class: 'muted' }, `${r.missionName} · ${formatTime(r.durationSec * 1000)}`),
        h('div', { class: 'awards' }, ...r.awards.map((aw) => h('div', { class: 'award' }, h('span', { class: 'big' }, aw.icon), h('b', null, aw.title), h('span', null, aw.name)))),
        h(
          'table',
          null,
          h('tr', null, h('th', null, '#'), h('th', null, 'Jugador'), h('th', null, 'Puntos'), h('th', null, 'XP'), h('th', null, '🔋'), h('th', null, '🤡'), h('th', null, '🪙')),
          ...r.players.map((p, i) => h('tr', { class: p.id === s.you ? 'me' : '' }, h('td', null, String(i + 1)), h('td', null, `${p.name}${p.levelAfter > p.levelBefore ? ` ⬆️ Nv ${p.levelAfter}` : ''}`), h('td', null, String(p.score)), h('td', null, `+${p.xp}`), h('td', null, String(p.cells)), h('td', null, String(p.pranks)), h('td', null, String(p.coins)))),
        ),
        me && me.newUnlocks.length ? h('p', { class: 'unlocks' }, `🎁 Desbloqueado: ${me.newUnlocks.join(', ')}`) : null,
        me && me.newAchievements.length ? h('p', { class: 'unlocks' }, `🏅 ¡Nuevo logro!`) : null,
        h('p', { class: 'small muted' }, 'Volvéis al lobby en unos segundos…'),
        button('Cerrar', () => ui.set({ results: null }), 'btn primary'),
      ),
    );
  };

  // ------------------------------------------------------------ pausa
  const renderPause = (s: GameUI) => {
    clear(pause);
    if (!s.paused) return;
    const isHost = s.room?.hostId === s.you;
    pause.append(
      h(
        'div',
        { class: 'pause panel' },
        h('h1', { class: 'title-small' }, 'Pausa'),
        h('p', { class: 'small muted' }, 'El juego sigue para los demás 😉'),
        button('▶ Continuar', () => ui.set({ paused: false }), 'btn primary big'),
        button('⚙️ Ajustes', () => openSettings(a.engineInfo()), 'btn big'),
        button('❓ Cómo jugar', openHowToPlay, 'btn big'),
        isHost && s.room && s.room.phase !== 'lobby' ? button('↩ Volver al lobby (anfitrión)', () => void connection.request('room:toLobby').then(() => ui.set({ paused: false })), 'btn big') : null,
        button('🚪 Salir al menú', () => (ui.set({ paused: false }), a.leave()), 'btn ghost big'),
      ),
    );
  };

  // ------------------------------------------------------------ estado (conexión, jugadores, ajustes)
  const renderStatus = () => {
    const c = connection.state.get();
    const s = ui.get();
    clear(statusBox);
    const color = s.mode === 'offline' ? 'gray' : c.status !== 'online' ? 'red' : c.rtt > 200 ? 'orange' : 'green';
    append(statusBox, [
      h('span', { class: `conn-dot ${color}`, title: c.status }, s.mode === 'offline' ? 'Sin conexión' : c.status === 'online' ? `${c.rtt} ms` : c.status === 'reconnecting' ? 'Reconectando…' : 'Desconectado'),
      s.room ? h('span', { class: 'chip' }, `👥 ${s.room.players.filter((p) => p.connected).length}/${s.room.config.maxPlayers}`) : null,
      button('⚙️', () => ui.set({ paused: true }), 'btn ghost small icon', { 'aria-label': 'Menú' }),
      ]);
  };

  // ------------------------------------------------------------ controles táctiles
  if (a.input.isTouch) {
    const stick = h('div', { class: 'stick' }, h('div', { class: 'knob' }));
    const knob = stick.firstChild as HTMLElement;
    let stickId: number | null = null;
    let lookId: number | null = null;
    let lx = 0;
    let ly = 0;
    const lookPad = h('div', { class: 'look-pad' });
    stick.addEventListener('pointerdown', (e) => {
      stickId = e.pointerId;
      stick.setPointerCapture(e.pointerId);
    });
    stick.addEventListener('pointermove', (e) => {
      if (e.pointerId !== stickId) return;
      const r = stick.getBoundingClientRect();
      const dx = (e.clientX - (r.left + r.width / 2)) / (r.width / 2);
      const dy = (e.clientY - (r.top + r.height / 2)) / (r.height / 2);
      const len = Math.min(1, Math.hypot(dx, dy));
      const ang = Math.atan2(dy, dx);
      a.input.touchMove = { x: Math.cos(ang) * len, y: -Math.sin(ang) * len };
      knob.style.transform = `translate(${Math.cos(ang) * len * 40}px, ${Math.sin(ang) * len * 40}px)`;
    });
    const end = () => {
      stickId = null;
      a.input.touchMove = { x: 0, y: 0 };
      knob.style.transform = '';
    };
    stick.addEventListener('pointerup', end);
    stick.addEventListener('pointercancel', end);
    lookPad.addEventListener('pointerdown', (e) => {
      lookId = e.pointerId;
      lx = e.clientX;
      ly = e.clientY;
    });
    lookPad.addEventListener('pointermove', (e) => {
      if (e.pointerId !== lookId) return;
      a.input.lookDX += (e.clientX - lx) * 1.6;
      a.input.lookDY += (e.clientY - ly) * 1.6;
      lx = e.clientX;
      ly = e.clientY;
    });
    lookPad.addEventListener('pointerup', () => (lookId = null));
    const tb = (label: string, fn: () => void, cls = '') => {
      const b = h('button', { class: `tbtn ${cls}`, type: 'button' }, label);
      b.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        fn();
      });
      return b;
    };
    touch.append(
      lookPad,
      stick,
      h(
        'div',
        { class: 'tbtns' },
        tb('⤒', () => a.input.queueJump(), 'jump'),
        tb('E', () => a.session()?.interactMobile()),
        tb('🫸', () => a.session()?.useAbility('push')),
        tb('★', () => {
          const me = ui.get().room?.players.find((p) => p.id === ui.get().you);
          if (me) a.session()?.useAbility(me.special);
        }),
        tb('💬', () => openChat(!chatOpen)),
      ),
    );
  }

  // ------------------------------------------------------------ suscripciones
  let prevMission = '';
  let prevRoom = '';
  const unsub = ui.subscribe((s, p) => {
    if (s.room !== p.room || s.you !== p.you || s.profile !== p.profile) {
      const key = JSON.stringify([s.room, s.profile?.blocked]);
      if (key !== prevRoom) {
        prevRoom = key;
        renderLobby(s);
        renderAbilities(s);
        renderScoreboard(s);
      }
    }
    if (s.mission !== p.mission || s.minigame !== p.minigame || s.mode !== p.mode) {
      const key = JSON.stringify([s.mission, s.minigame?.scores, s.mode]);
      if (key !== prevMission) {
        prevMission = key;
        renderMission(s);
      }
    }
    if (s.chat !== p.chat) renderChat(s);
    if (s.scoreboard !== p.scoreboard) renderScoreboard(s);
    if (s.results !== p.results) renderResults(s);
    if (s.paused !== p.paused) {
      renderPause(s);
      a.input.enabled = !s.paused && !chatOpen;
      if (s.paused) a.input.unlock();
    }
    if (s.prompt !== p.prompt) {
      prompt.textContent = s.prompt ? `${keyLabel(settings.get().bindings.interact)} — ${s.prompt}` : '';
      prompt.classList.toggle('show', !!s.prompt);
    }
    if (s.profile !== p.profile) renderEmotes();
  });
  const unsubConn = connection.state.subscribe(renderStatus);
  const offPress = a.input.on('press', (act) => {
    if (act === 'chat' && ui.get().mode === 'online') openChat(true);
    if (act === 'scoreboard') ui.set({ scoreboard: !ui.get().scoreboard });
  });
  const offEsc = a.input.on('escape', () => {
    if (chatOpen) return openChat(false);
    if (document.querySelector('.modal-backdrop')) return;
    if (ui.get().results) return ui.set({ results: null });
    ui.set({ paused: !ui.get().paused });
  });
  const s0 = ui.get();
  renderLobby(s0);
  renderMission(s0);
  renderAbilities(s0);
  renderChat(s0);
  renderEmotes();
  renderStatus();

  // ------------------------------------------------------------ bucle de alta frecuencia
  const wpEls: HTMLElement[] = [];
  const flEls = new Map<number, HTMLElement>();
  let raf = 0;
  let lastCountdown = '';
  const tick = () => {
    raf = requestAnimationFrame(tick);
    const s = ui.get();
    const now = connection.serverNow();
    // Temporizador de misión
    const timer = missionBox.querySelector('.timer') as HTMLElement | null;
    if (timer) {
      const left = Number(timer.dataset.timer) - now;
      timer.textContent = formatTime(left);
      timer.classList.toggle('urgent', left < 60_000);
    }
    // Minijuego / cuenta atrás / evento
    let msg = '';
    if (s.room?.phase === 'countdown' && s.room.phaseEndsAt) {
      const n = Math.ceil((s.room.phaseEndsAt - now) / 1000) - 1;
      msg = n > 0 ? String(n) : '¡YA!';
    } else if (s.minigame) msg = '';
    if (msg !== lastCountdown) {
      lastCountdown = msg;
      center.textContent = msg;
      center.classList.toggle('show', !!msg);
      center.classList.remove('pop');
      void center.offsetWidth;
      if (msg) center.classList.add('pop');
    }
    const ev = GameSession.eventLabel(s.modifiers);
    const mg = s.minigame;
    banner.textContent = ev && ev.until && ev.until > now ? `${ev.icon} ${ev.text} · ${Math.ceil((ev.until - now) / 1000)} s` : mg ? `🪙 ${formatTime(mg.endsAt - now)}` : '';
    banner.classList.toggle('show', !!banner.textContent);

    // Enfriamientos
    abilities.querySelectorAll<HTMLElement>('.ability').forEach((el) => {
      const id = el.dataset.ability as keyof typeof ABILITIES;
      const ready = s.cooldowns[id] ?? 0;
      const left = ready - now;
      const frac = left > 0 ? left / (ABILITIES[id].cooldown * 1000) : 0;
      el.style.setProperty('--cd', String(Math.min(1, frac)));
      (el.querySelector('.cd') as HTMLElement).textContent = left > 0 ? String(Math.ceil(left / 1000)) : '';
      el.classList.toggle('ready', left <= 0);
    });

    // Indicadores de objetivo (se pegan al borde si están fuera de la pantalla)
    const wps = live.waypoints;
    while (wpEls.length < wps.length) {
      const el = h('div', { class: 'wp' });
      waypointLayer.append(el);
      wpEls.push(el);
    }
    const W = window.innerWidth;
    const H = window.innerHeight;
    wpEls.forEach((el, i) => {
      const wp = wps[i];
      if (!wp) return void (el.style.display = 'none');
      const p = live.project(wp.x, wp.y, wp.z);
      let x = p.x;
      let y = p.y;
      const off = p.behind || x < 0 || x > W || y < 0 || y > H;
      if (p.behind) {
        x = W - x;
        y = H - 40;
      }
      x = Math.max(30, Math.min(W - 30, x));
      y = Math.max(80, Math.min(H - 120, y));
      el.style.display = '';
      el.style.transform = `translate(${x}px, ${y}px)`;
      el.classList.toggle('edge', off);
      if (el.dataset.k !== wp.icon + wp.label) {
        el.dataset.k = wp.icon + wp.label;
        el.textContent = wp.icon;
        el.title = wp.label;
      }
    });

    // Textos flotantes
    const tnow = performance.now();
    live.floaters = live.floaters.filter((f) => tnow - f.born < 1400);
    for (const [id, el] of flEls)
      if (!live.floaters.some((f) => f.id === id)) {
        el.remove();
        flEls.delete(id);
      }
    for (const f of live.floaters) {
      let el = flEls.get(f.id);
      if (!el) {
        el = h('div', { class: 'floater', style: { color: f.color } }, f.text);
        floaterLayer.append(el);
        flEls.set(f.id, el);
      }
      const age = (tnow - f.born) / 1400;
      const p = live.project(f.x, f.y + age * 1.2, f.z);
      el.style.display = p.behind ? 'none' : '';
      el.style.transform = `translate(${p.x}px, ${p.y}px) scale(${1 + age * 0.3})`;
      el.style.opacity = String(1 - age);
    }
    paint.classList.toggle('show', tnow < s.paintedUntil && !settings.get().reduceEffects);
    fps.textContent = settings.get().showFps ? `${live.fps} FPS` : '';
  };
  tick();

  return () => {
    cancelAnimationFrame(raf);
    unsub();
    unsubConn();
    offPress();
    offEsc();
    root.remove();
    audio.intensity = 'menu';
  };
}
